/**
 * Common React-Query options builder for polled endpoints.
 *
 * Extracts the shape every feature repeats:
 *   • enabled: skip when a required key (agent id, org id, ...) is missing
 *   • staleTime: how long the cache is considered fresh
 *   • placeholderData: keep previous data during background refetch
 *   • refetchInterval: parent-controlled poll (0 disables — TanStack convention)
 *
 * Usage:
 *   useQuery(buildQueryOpts({
 *     key: systemMonitoringKeys.health(agentId),
 *     fn: () => getHealth(agentId),
 *     enabled: agentId !== "",
 *     staleMs: 15_000,
 *     defaultPollMs: 30_000,
 *     opts,
 *   }));
 */
import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";

/** Public shape of the poll override that all polled hooks accept from their parent. */
export interface PollOpts {
  refetchIntervalMs?: number | undefined;
}

/**
 * Translate a caller-supplied `refetchIntervalMs` into TanStack's
 * `refetchInterval` value:
 *   undefined  → use the hook's default cadence
 *   > 0        → poll every N ms
 *   0 or less  → disable polling (false)
 */
export const asPoll = (
  ms: number | undefined,
  fallback: number | false = false,
): number | false => (ms === undefined ? fallback : ms > 0 ? ms : false);

export interface BuildQueryOptsInput<T, K extends QueryKey> {
  key: K;
  fn: () => Promise<T>;
  enabled: boolean;
  staleMs: number;
  defaultPollMs?: number | false | undefined;
  opts?: PollOpts | undefined;
}

/**
 * Assemble the standard useQuery options bag. The returned object is a
 * plain UseQueryOptions — callers pass it straight to `useQuery(...)`, and
 * can spread more properties on the way if they need to override.
 */
export function buildQueryOpts<T, K extends QueryKey>(
  input: BuildQueryOptsInput<T, K>,
): UseQueryOptions<T, Error, T, K> {
  const { key, fn, enabled, staleMs, defaultPollMs = false, opts } = input;
  return {
    queryKey: key,
    queryFn: fn,
    enabled,
    staleTime: staleMs,
    placeholderData: (prev) => prev,
    refetchInterval: asPoll(opts?.refetchIntervalMs, defaultPollMs),
  };
}
